import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import BidItemCard from "./BidItemCard";
import COCard from "./COCard";
import LocationCard from "./LocationCard";
import bidItemActions from "../../_actions/bidItemActions";
import ccoActions from "../../_actions/ccoActions";
import locationActions from "../../_actions/locationActions";
import projActions from "../../_actions/projActions";
import userActions from "../../_actions/userActions";

export default function ValidateDataModal(props) {
  const { showValidateDataModal, toggleValidateDataModal, projectId } = props;
  const dispatch = useDispatch();

  const [pendingData, setPendingData] = useState(null);
  const [activeTab, setActiveTab] = useState("BI");

  const getPendingData = useCallback(async () => {
    const data = await dispatch(projActions.getPendingData(projectId));
    //get all UploadedByUserIds from BidItems, Ccos, and Locations
    const bidItemsUserIds = data.bidItems.map((item) => item.uploadedByUserId) || [];
    const ccosUserIds = data.ccos.map((cco) => cco.uploadedByUserId) || [];
    const locationsUserIds = data.locations.map((location) => location.uploadedByUserId) || [];
    const allUserIds = [...bidItemsUserIds, ...ccosUserIds, ...locationsUserIds];
    const uniqueUserIds = [...new Set(allUserIds)];
    //get userName from userId
    const userNames = await Promise.all(
      uniqueUserIds.map((id) => dispatch(userActions.getName(id)))
    );
    const userMap = Object.fromEntries(
      uniqueUserIds.map((id, index) => [id, userNames[index]])
    );

    const bidItemsWithUserName = (data.bidItems || []).map((bidItem) => ({
      ...bidItem,
      uploadedBy: userMap[bidItem.uploadedByUserId],
    }));
    const ccosWithUserName = (data.ccos || []).map((cco) => ({
      ...cco,
      uploadedBy: userMap[cco.uploadedByUserId],
    }));
    const locationsWithUserName = (data.locations || []).map((location) => ({
      ...location,
      uploadedBy: userMap[location.uploadedByUserId],
    }));

    setPendingData({
      ...data, 
      bidItems: bidItemsWithUserName,
      ccos: ccosWithUserName,
      locations: locationsWithUserName,
     });
  }, [dispatch, projectId]);

  useEffect(() => {
    getPendingData();
    dispatch(bidItemActions.getAll(projectId));
    dispatch(ccoActions.getAll(projectId));
    dispatch(locationActions.getAll(projectId));
  }, [dispatch, projectId, getPendingData]);

  //get project item list and sort by item #
  const projItemList = useSelector((state) => {
    const list = state.bidItems.projItemLists[projectId] || [];
    return [...list].sort((a, b) => a.itemNo - b.itemNo);
  });
  const projCcoList = useSelector((state) => state.ccos.projCcoLists[projectId] || []);
  const projLocationList = useSelector((state) => state.locations.projLocationLists[projectId] || []);

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const groupBy = (array, key) => {
    return (array || []).reduce((result, currentValue) => {
      const groupKey = currentValue[key];
      if (!result[groupKey]) {
        result[groupKey] = [];
      }
      result[groupKey].push(currentValue);
      return result;
    }, {});
  };

  //group BidItems,CCOs,Locations by it's number
  const groupedData = useMemo(() => ({
    BidItems: groupBy(pendingData?.bidItems || [], "itemNo"),
    CCOs: groupBy(pendingData?.ccos || [], "ccoNo"),
    Locations: groupBy(pendingData?.locations || [], "name"),
  }), [pendingData]);

  const countItems = (items) => items.length;

  const bidItemCount = Object.values(groupedData.BidItems).reduce(
    (total, items) => total + countItems(items),
    0
  );

  const CCO_Count = Object.values(groupedData.CCOs).reduce(
    (total, items) => total + countItems(items),
    0
  );

  const locationCount = Object.values(groupedData.Locations).reduce(
    (total, items) => total + countItems(items),
    0
  );

  const [isLoading, setIsLoading] = useState(false);

  const handleValidateItems = async (projectId, caseBItems, activeTab) => {
    setIsLoading(true);
    try {
      await dispatch(projActions.updatePendingData(projectId, caseBItems));
      await Promise.all([
        getPendingData(),
        (async () => {
          switch (activeTab) {
            case 'BI':
              await dispatch(bidItemActions.getAll(projectId));
              break;
            case 'CCO':
              await dispatch(ccoActions.getAll(projectId));
              break;
            case 'LOC':
              await dispatch(locationActions.getAll(projectId));
          };
        })()
      ]);
    } finally {
      setIsLoading(false);
    }
  };


  const circleStyle = {
    display: "inline-block",
    backgroundColor: "red",
    color: "white",
    borderRadius: "50%",
    width: "20px",
    height: "20px",
    lineHeight: "20px",
    textAlign: "center",
    fontSize: "12px",
    marginLeft: "5px",
    fontWeight: "bold",
  };

  const tabStyle = {
    cursor: "pointer",
  };

  return (
    <Modal isOpen={showValidateDataModal} toggle={toggleValidateDataModal} size="lg">
      <ModalHeader toggle={toggleValidateDataModal}>Validate Data</ModalHeader>
      <ModalBody>
        <Nav tabs>
          <NavItem>
            <NavLink
              style={tabStyle}
              className={classnames({ active: activeTab === "BI" })}
              onClick={() => toggleTab("BI")}
            >
              Bid Item <span style={circleStyle}>{bidItemCount}</span>
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              style={tabStyle}
              className={classnames({ active: activeTab === "CCO" })}
              onClick={() => toggleTab("CCO")}
            >
              CCO <span style={circleStyle}>{CCO_Count}</span>
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              style={tabStyle}
              className={classnames({ active: activeTab === "LOC" })}
              onClick={() => toggleTab("LOC")}
            >
              Location <span style={circleStyle}>{locationCount}</span>
            </NavLink>
          </NavItem>
        </Nav>
        <TabContent activeTab={activeTab}>
          <TabPane tabId="BI">
            {/*<label style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word', overflow: 'hidden', maxWidth: '100%', display: 'block' }}>
            Pending Data: {JSON.stringify(projLocationList, null, 2)}
            </label> */}
            {Object.entries(groupedData.BidItems).map(([itemNo, items]) => (
              <BidItemCard
                key={itemNo}
                itemNo={itemNo}
                items={items}
                projItemList={projItemList}
                onValidate={handleValidateItems}
                projectId={projectId}
                activeTab={activeTab}
              />
            ))}
          </TabPane>
          <TabPane tabId="CCO">
            {Object.entries(groupedData.CCOs).map(([ccoNo, items]) => (
              <COCard
                key={ccoNo}
                ccoNo={ccoNo}
                items={items}
                projCcoList={projCcoList}
                onValidate={handleValidateItems}
                projectId={projectId}
                activeTab={activeTab}
              />
            ))}
          </TabPane>
          <TabPane tabId="LOC">
            {Object.entries(groupedData.Locations).map(([name, items]) => (
              <LocationCard
                key={name}
                name={name}
                items={items}
                projLocationList={projLocationList}
                onValidate={handleValidateItems}
                projectId={projectId}
                activeTab={activeTab}
              />
            ))}
          </TabPane>
        </TabContent>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" disabled={isLoading} onClick={toggleValidateDataModal}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
}
